import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Button,
    Card,
    Col,
    DatePicker,
    Descriptions,
    Drawer,
    Dropdown,
    Form,
    Input,
    message,
    Modal,
    Row,
    Segmented,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
    Collapse
} from "antd";
import {
    CheckCircleOutlined,
    DownOutlined,
    EditOutlined,
    EyeOutlined,
    MoreOutlined,
    PlusOutlined,
    ReloadOutlined,
    RightOutlined,
    SearchOutlined,
    SendOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import {useHistory} from "react-router-dom";
import axiosinstance from "util/Api";
import {CisUI} from "../../../../util/CISUI";
import "./AssetRequestList.css";

const {Title, Text} = Typography;
const {RangePicker} = DatePicker;
const {TextArea} = Input;
const Panel = Collapse.Panel;

const LEVEL_LABELS = {
    LOCAL: "Local",
    DISTRICT: "District",
    REGION: "Area",
    AREA: "Area",
    NATIONAL: "National"
};

const STATUS_COLORS = {
    DRAFT: "default",
    SUBMITTED: "blue",
    RESUBMITTED: "cyan",
    UNDER_REVIEW: "processing",
    FORWARDED: "geekblue",
    RETURNED: "orange",
    APPROVED: "green",
    REJECTED: "red",
    CANCELLED: "default"
};

const PRIORITY_COLORS = {
    CRITICAL: "red",
    URGENT: "volcano",
    HIGH: "orange",
    MEDIUM: "blue",
    LOW: "default"
};

// One hex per antd colour keyword, reused for both the tag "dot" and the
// coloured accent strip drawn along the left edge of each table row so a
// request's status/priority reads at a glance without opening it.
const ACCENT_HEX = {
    default: "#bfbfbf", blue: "#1677ff", cyan: "#13c2c2", processing: "#1677ff",
    geekblue: "#2f54eb", orange: "#fa8c16", green: "#52c41a", red: "#ff4d4f",
    volcano: "#fa541c", gold: "#faad14", purple: "#722ed1"
};

const QUICK_FILTERS = [
    {label: "All Requests", value: "ALL"},
    {label: "Submitted", value: "SUBMITTED"},
    {label: "Under Review", value: "UNDER_REVIEW"},
    {label: "Returned", value: "RETURNED"},
    {label: "Approved", value: "APPROVED"},
    {label: "Rejected", value: "REJECTED"}
];

const normalize = (value) => String(value || "").trim().toUpperCase();

const prettyStatus = (value) => String(value || "-").replace(/_/g, " ");

const money = (value) =>
    Number(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });


const entityText = (record, type) => {
    const candidates = {
        local: [
            record.local_name,
            record.local_entity_name,
            record.local_church_name,
            record.local_title,
            record.local
        ],
        district: [
            record.district_name,
            record.district_entity_name,
            record.district_title,
            record.district
        ],
        region: [
            record.region_name,
            record.area_name,
            record.region_entity_name,
            record.region_title,
            record.region
        ]
    };

    const value = (candidates[type] || []).find(
        (item) => item !== undefined && item !== null && String(item).trim() !== ""
    );

    if (value !== undefined) return String(value);

    const id = record[`${type === "region" ? "region" : type}_id`];
    return id ? `ID: ${id}` : "-";
};

const entitySearchText = (record) =>
    [
        entityText(record, "local"),
        entityText(record, "district"),
        entityText(record, "region")
    ].filter((x) => x && x !== "-").join(" • ");

const entityBreadcrumb = (record) =>
    [
        entityText(record, "local"),
        entityText(record, "district"),
        entityText(record, "region")
    ].filter((x) => x && x !== "-").join(" › ") || "-";


const dateTimeText = (value) => {
    if (!value) return "-";
    const d = dayjs(value);
    return d.isValid() ? d.format("DD MMM YYYY, hh:mm A") : "-";
};

const userName = (record, prefix) => {
    const candidates = [
        record[`${prefix}_name`],
        record[`${prefix}_full_name`],
        record[`${prefix}_user_name`],
        record[`${prefix}_username`]
    ];
    const found = candidates.find(
        value => value !== undefined && value !== null && String(value).trim() !== ""
    );
    return found ? String(found) : "-";
};

const approvalDisplay = (record) => {
    const status = normalize(record.status);

    if (record.approved_by_name || record.approved_by_full_name) {
        return {
            name: record.approved_by_name || record.approved_by_full_name,
            at: record.approved_at || record.last_approved_at || null,
            pending: false
        };
    }

    if (status === "APPROVED") {
        return {
            name: record.last_action_by_name || "-",
            at: record.last_action_at || null,
            pending: false
        };
    }

    return {
        name: `Pending ${LEVEL_LABELS[normalize(record.current_level)] || record.current_level || ""}`,
        at: null,
        pending: true
    };
};

/** The detail panel revealed when a row is expanded — everything that used
 *  to occupy its own column (created/updated by+at, full entity hierarchy,
 *  last workflow action) now lives here so the main table stays scannable. */
const ExpandedRow = ({record}) => (
    <div className="arl-expand-panel">
        <Descriptions bordered size="small" column={{xs: 1, sm: 2, md: 3}}>
            <Descriptions.Item label="Local">{entityText(record, "local")}</Descriptions.Item>
            <Descriptions.Item label="District">{entityText(record, "district")}</Descriptions.Item>
            <Descriptions.Item label="Area">{entityText(record, "region")}</Descriptions.Item>
            <Descriptions.Item label="Created By">{userName(record, "created_by")}</Descriptions.Item>
            <Descriptions.Item label="Created At">{dateTimeText(record.created_at)}</Descriptions.Item>
            <Descriptions.Item label="Updated By">
                {userName(record, "updated_by") !== "-" ? userName(record, "updated_by") : userName(record, "created_by")}
            </Descriptions.Item>
            <Descriptions.Item label="Updated At">{dateTimeText(record.updated_at || record.created_at)}</Descriptions.Item>
            <Descriptions.Item label="Last Action">
                {record.last_action ? prettyStatus(record.last_action) : "No workflow action yet"}
            </Descriptions.Item>
            <Descriptions.Item label="Last Action By">{record.last_action_by_name || "-"}</Descriptions.Item>
            <Descriptions.Item label="Last Action At">
                {dateTimeText(record.last_action_at || record.updated_at || record.created_at)}
            </Descriptions.Item>
        </Descriptions>
    </div>
);

const AssetRequestList = () => {
    const history = useHistory();
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0
    });
    const [activeStatus, setActiveStatus] = useState("ALL");
    const [statusCounts, setStatusCounts] = useState({
        ALL: 0, SUBMITTED: 0, UNDER_REVIEW: 0, RETURNED: 0, APPROVED: 0, REJECTED: 0
    });

    const [selected, setSelected] = useState(null);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [approvalModal, setApprovalModal] = useState(false);
    const [approvalComments, setApprovalComments] = useState("");

    const getStatusCounts = useCallback(async (override = null) => {
        try {
            // Counts must NOT come from the current page. Query each status using
            // per_page=1 so Laravel paginator.total remains the authoritative count.
            const values = override || form.getFieldsValue();
            const base = {
                per_page: 1,
                search: values.search || undefined,
                current_level: values.current_level || undefined,
                asset_type: values.asset_type || undefined,
                priority_level: values.priority_level || undefined,
                entity: values.entity || undefined
            };
            if (values.request_date && values.request_date.length === 2) {
                base.from_date = dayjs(values.request_date[0]).format("YYYY-MM-DD");
                base.to_date = dayjs(values.request_date[1]).format("YYYY-MM-DD");
            }

            const statuses = ["SUBMITTED", "UNDER_REVIEW", "RETURNED", "APPROVED", "REJECTED"];
            const [allRes, ...statusRes] = await Promise.all([
                axiosinstance.get("/assets/assetrequest", {params: {...base, page: 1}}),
                ...statuses.map(status =>
                    axiosinstance.get("/assets/assetrequest", {params: {...base, page: 1, status}})
                )
            ]);

            const totalOf = (res) => {
                const payload = res?.data?.data;
                if (payload && !Array.isArray(payload)) {
                    return Number(payload.total ?? payload.meta?.total ?? payload.items?.length ?? 0);
                }
                return Number(res?.data?.total ?? (Array.isArray(payload) ? payload.length : 0));
            };

            const next = {ALL: totalOf(allRes)};
            statuses.forEach((status, i) => { next[status] = totalOf(statusRes[i]); });
            setStatusCounts(prev => ({...prev, ...next}));
        } catch (error) {
            console.error("Asset request status count error:", error);
        }
    }, [form]);

    const getList = useCallback(async (page = 1, pageSize = 20, override = null) => {
        try {
            setLoading(true);

            const values = override || form.getFieldsValue();
            const params = {
                page,
                per_page: pageSize,
                search: values.search || undefined,
                status: values.status || undefined,
                current_level: values.current_level || undefined,
                asset_type: values.asset_type || undefined,
                priority_level: values.priority_level || undefined,
                entity: values.entity || undefined
            };

            if (values.request_date && values.request_date.length === 2) {
                params.from_date = dayjs(values.request_date[0]).format("YYYY-MM-DD");
                params.to_date = dayjs(values.request_date[1]).format("YYYY-MM-DD");
            }

            const res = await axiosinstance.get("/assets/assetrequest", {params});

            if (res.data.status !== 1) {
                message.warning(res.data.msg || "Unable to load asset requests.");
                setData([]);
                return;
            }

            const payload = res.data.data;
            let rows = [];
            let total = 0;
            let current = page;
            let size = pageSize;

            // Supports both a Laravel paginator and a plain array response.
            if (Array.isArray(payload)) {
                rows = payload;
                total = Number(res.data.total || payload.length);
            } else if (payload && Array.isArray(payload.data)) {
                rows = payload.data;
                total = Number(payload.total || rows.length);
                current = Number(payload.current_page || page);
                size = Number(payload.per_page || pageSize);
            } else if (payload && Array.isArray(payload.items)) {
                rows = payload.items;
                total = Number(payload.total || rows.length);
            }

            setData(rows);
            setPagination({
                current,
                pageSize: size,
                total
            });
        } catch (error) {
            console.error("Asset request list error:", error);
            message.error(
                error?.response?.data?.msg ||
                error?.response?.data?.message ||
                "Unable to load asset requests."
            );
        } finally {
            setLoading(false);
        }
    }, [form]);

    useEffect(() => {
        getList(1, pagination.pageSize);
        getStatusCounts({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = () => {
        const values = form.getFieldsValue();
        getList(1, pagination.pageSize, values);
        getStatusCounts(values);
    };

    const handleReset = () => {
        form.resetFields();
        setActiveStatus("ALL");
        getList(1, pagination.pageSize, {});
        getStatusCounts({});
    };

    const handleQuickFilter = (value) => {
        setActiveStatus(value);
        const nextStatus = value === "ALL" ? undefined : value;
        form.setFieldsValue({status: nextStatus});
        getList(1, pagination.pageSize, {...form.getFieldsValue(), status: nextStatus});
    };

    const openRequest = (record) => {
        setSelected(record);
        setOpenDrawer(true);
    };

    const editRequest = (record) => {
        history.push(`/assets/editassetrequest/${record.id}`);
    };

    const canEdit = (record) => {
        const level = normalize(record.current_level);
        const status = normalize(record.status);
        return level === "LOCAL" && ["DRAFT", "RETURNED"].includes(status);
    };

    const getApprovalAction = (record) => {
        const level = normalize(record.current_level);
        const status = normalize(record.status);

        if (["APPROVED", "REJECTED", "CANCELLED", "DRAFT"].includes(status)) {
            return null;
        }

        if (level === "DISTRICT") {
            return {
                label: "District Approve & Forward",
                endpoint: `/assets/assetrequestforward/${record.id}`,
                nextLevel: "Area",
                action: "forward"
            };
        }

        if (level === "REGION" || level === "AREA") {
            return {
                label: "Area Approve & Forward",
                endpoint: `/assets/assetrequestforward/${record.id}`,
                nextLevel: "National",
                action: "forward"
            };
        }

        if (level === "NATIONAL") {
            return {
                label: "National Final Approve",
                endpoint: `/assets/assetrequestapprove/${record.id}`,
                nextLevel: null,
                action: "approve"
            };
        }

        return null;
    };

    const showApproval = (record) => {
        const approval = getApprovalAction(record);
        if (!approval) {
            message.warning("No approval action is available at the current workflow level.");
            return;
        }
        setSelected(record);
        setApprovalComments("");
        setApprovalModal(true);
    };

    const confirmApproval = async () => {
        if (!selected) return;

        const approval = getApprovalAction(selected);
        if (!approval) return;

        try {
            setActionLoading(true);

            const payload = approval.action === "forward"
                ? {
                    feedback: approvalComments || `${LEVEL_LABELS[normalize(selected.current_level)] || selected.current_level} assessment completed.`,
                    comments: approvalComments || `${LEVEL_LABELS[normalize(selected.current_level)] || selected.current_level} approved and forwarded to ${approval.nextLevel}.`
                }
                : {
                    comments: approvalComments || "National review completed and asset request approved."
                };

            const res = await axiosinstance.post(approval.endpoint, payload);

            if (res.data.status !== 1) {
                message.warning(res.data.msg || "Unable to complete approval.");
                return;
            }

            message.success(
                approval.action === "approve"
                    ? "Asset request approved successfully."
                    : `Asset request approved and forwarded to ${approval.nextLevel}.`
            );

            setApprovalModal(false);
            setSelected(null);
            await getList(pagination.current, pagination.pageSize);
            await getStatusCounts(form.getFieldsValue());
        } catch (error) {
            console.error("Approval error:", error);
            message.error(
                error?.response?.data?.msg ||
                error?.response?.data?.message ||
                "Unable to complete approval."
            );
        } finally {
            setActionLoading(false);
        }
    };

    /** Row action menu, built with antd's `items` API (the `<Menu>` /
     *  `overlay=` combo used previously is deprecated in favour of this). */
    const buildActionItems = useCallback((record) => {
        const approval = getApprovalAction(record);
        const items = [
            {
                key: "open",
                icon: <EyeOutlined/>,
                label: "Open Details",
                onClick: () => history.push(`/assets/assetrequestdetail/${record.id}`)
            },
            {
                key: "quick",
                icon: <EyeOutlined/>,
                label: "Quick View",
                onClick: () => openRequest(record)
            },
            {
                key: "edit",
                icon: <EditOutlined/>,
                label: "Edit",
                disabled: !canEdit(record),
                onClick: () => editRequest(record)
            }
        ];

        if (approval) {
            items.push({type: "divider"});
            items.push({
                key: "approve",
                icon: normalize(record.current_level) === "NATIONAL" ? <CheckCircleOutlined/> : <SendOutlined/>,
                label: approval.label,
                onClick: () => showApproval(record)
            });
        }

        return items;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history]);

    // Condensed to the columns someone triaging a queue actually scans first;
    // everything else (audit trail, full entity chain) lives in the row's
    // expand panel instead of eating horizontal space for every row.
    const columns = useMemo(() => [
        {
            title: "SL",
            key: "sl",
            width: 50,
            fixed: "left",
            align: "center",
            render: (_, __, index) =>
                (pagination.current - 1) * pagination.pageSize + index + 1
        },
        {
            title: "Request",
            key: "request",
            width: 160,
            fixed: "left",
            render: (_, record) => (
                <div>
                    <Button
                        type="link"
                        className="arl-request-link"
                        onClick={() => history.push(`/assets/assetrequestdetail/${record.id}`)}
                    >
                        {record.request_no || `AR-${record.id}`}
                    </Button>
                    <div className="arl-mt-3">
                        <Text className="arl-sub">
                            {record.request_date
                                ? dayjs(record.request_date).format(CisUI().dateFormat)
                                : "-"}
                        </Text>
                    </div>
                </div>
            )
        },
        {
            title: "Asset & Priority",
            key: "asset_type",
            width: 190,
            render: (_, record) => (
                <div>
                    <Text strong ellipsis>{record.asset_type || "-"}</Text>
                    <div className="arl-mt-3">
                        <Space size={4} wrap>
                            <Tag
                                className={`arl-tag-dot arl-tag-priority arl-accent-${PRIORITY_COLORS[normalize(record.priority_level)] || "default"}`}
                                color={PRIORITY_COLORS[normalize(record.priority_level)] || "default"}
                                style={{margin: 0, fontSize: 10}}
                            >
                                {record.priority_level || "-"}
                            </Tag>
                            <Text className="arl-sub">
                                {record.acquisition_method || "-"}
                            </Text>
                        </Space>
                    </div>
                </div>
            )
        },
        {
            title: "Entity",
            key: "entity",
            width: 230,
            render: (_, record) => (
                <Tooltip
                    title={
                        <div>
                            <div>Local: {entityText(record, "local")}</div>
                            <div>District: {entityText(record, "district")}</div>
                            <div>Area: {entityText(record, "region")}</div>
                        </div>
                    }
                >
                    <Text ellipsis className="arl-entity-breadcrumb">
                        {entityBreadcrumb(record)}
                    </Text>
                </Tooltip>
            )
        },
        {
            title: "Financial",
            key: "financial",
            width: 150,
            align: "right",
            render: (_, record) => (
                <div>
                    <Text strong>{money(record.estimated_cost)}</Text>
                    <div>
                        <Text className="arl-sub">
                            Due {record.required_by_date
                            ? dayjs(record.required_by_date).format(CisUI().dateFormat)
                            : "-"}
                        </Text>
                    </div>
                </div>
            )
        },
        {
            title: "Workflow",
            key: "workflow",
            width: 330,
            render: (_, record) => (
                <div>
                    <Space size={5} wrap>
                        <Tag
                            className={`arl-tag-dot arl-tag-status arl-accent-${STATUS_COLORS[normalize(record.status)] || "default"}`}
                            color={STATUS_COLORS[normalize(record.status)] || "default"}
                            style={{margin: 0}}
                        >
                            {String(record.status || "-").replace(/_/g, " ")}
                        </Tag>
                        <Tag className="arl-level-tag" color="purple">
                            <span className="arl-level-label">Level</span>
                            <span>{LEVEL_LABELS[normalize(record.current_level)] || record.current_level || "-"}</span>
                        </Tag>
                    </Space>
                    <div className="arl-mt-5">
                        <Text className="arl-sub" ellipsis>
                            {record.last_action ? prettyStatus(record.last_action) : "No workflow action yet"}
                        </Text>
                    </div>
                </div>
            )
        },
        {
            title: "Approver",
            key: "approved_by",
            width: 170,
            render: (_, record) => {
                const approved = approvalDisplay(record);
                return approved.pending ? (
                    <Tag className="arl-tag-dot arl-accent-gold" color="gold" style={{margin: 0}}>
                        {approved.name}
                    </Tag>
                ) : (
                    <div>
                        <Space size={5}>
                            <CheckCircleOutlined style={{color: "#52c41a"}}/>
                            <Text strong style={{fontSize: 12}}>{approved.name}</Text>
                        </Space>
                        <div className="arl-mt-3">
                            <Text className="arl-sub-xs">{dateTimeText(approved.at)}</Text>
                        </div>
                    </div>
                );
            }
        },
        {
            title: "Action",
            key: "action",
            width: 90,
            fixed: "right",
            align: "center",
            render: (_, record) => (
                <Dropdown menu={{items: buildActionItems(record)}} trigger={["click"]}>
                    <Tooltip title="Request actions">
                    <Button className="arl-action-btn" size="small" aria-label="Request actions">
                        <MoreOutlined/>
                    </Button>
                </Tooltip>
                </Dropdown>
            )
        }
    ], [pagination, buildActionItems]);

    const tableChange = (pager) => {
        getList(pager.current, pager.pageSize);
    };

    return (
        <div className="arl-page">
            <Card bordered={false} className="arl-header-card">
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col>
                        <Title level={3} className="arl-header-title">
                            Asset Acquisition Requests
                        </Title>
                        <Text className="arl-header-sub">
                            Search, open, edit and process requests according to the current approval level.
                        </Text>
                    </Col>

                    <Col>
                        <Space wrap className="arl-header-actions">
                            <Button
                                icon={<ReloadOutlined/>}
                                onClick={() => getList(pagination.current, pagination.pageSize)}
                            >
                                Refresh
                            </Button>

                            <Button
                                type="primary"
                                icon={<PlusOutlined/>}
                                onClick={() => history.push("/assets/newassetrequest")}
                            >
                                New Request
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Card className="advance-search-card arl-search-card">
                <Collapse
                    accordion
                    ghost
                    expandIconPosition="right"
                    defaultActiveKey={['1']} >
                    <Panel header="Advance Search"  key="1">
                        <Form
                            key={5}
                            form={form}
                            {...CisUI().formItemLayout}
                            name="advanced_search"
                            className="ant-advanced-search-form"
                            onFinish={handleSearch}
                        >
                            <Row gutter={[12, 0]}>
                                <Col xs={24} md={8} lg={5}>
                                    <Form.Item label="Search" name="search">
                                        <Input
                                            allowClear
                                            prefix={<SearchOutlined style={{color: "#bfbfbf"}}/>}
                                            placeholder="Request no, asset type, description..."
                                            onPressEnter={handleSearch}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={12} md={4} lg={3}>
                                    <Form.Item label="Level" name="current_level">
                                        <Select
                                            allowClear
                                            placeholder="All Levels"
                                            options={[
                                                {label: "Local", value: "LOCAL"},
                                                {label: "District", value: "DISTRICT"},
                                                {label: "Area", value: "AREA"},
                                                {label: "National", value: "NATIONAL"}
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={12} md={4} lg={3}>
                                    <Form.Item label="Status" name="status">
                                        <Select
                                            allowClear
                                            placeholder="All Status"
                                            onChange={(value) => setActiveStatus(value || "ALL")}
                                            options={[
                                                "DRAFT",
                                                "SUBMITTED",
                                                "RESUBMITTED",
                                                "UNDER_REVIEW",
                                                "FORWARDED",
                                                "RETURNED",
                                                "APPROVED",
                                                "REJECTED",
                                                "CANCELLED"
                                            ].map(value => ({
                                                label: value.replace(/_/g, " "),
                                                value
                                            }))}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={12} md={4} lg={3}>
                                    <Form.Item label="Priority" name="priority_level">
                                        <Select
                                            allowClear
                                            placeholder="All Priority"
                                            options={["Critical", "Urgent", "High", "Medium", "Low"].map(value => ({
                                                label: value,
                                                value
                                            }))}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={8} lg={5}>
                                    <Form.Item label="Request Date" name="request_date">
                                        <RangePicker
                                            style={{width: "100%"}}
                                            format={CisUI().dateFormat}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={8} lg={4}>
                                    <Form.Item label=" ">
                                        <Space>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                icon={<SearchOutlined/>}
                                            >
                                                Search
                                            </Button>
                                            <Button onClick={handleReset}>
                                                Reset
                                            </Button>
                                        </Space>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </Panel>
                </Collapse>
            </Card>

            <Card
                bordered={false}
                className="arl-table-card"
                style={{marginTop: 16}}
                title={
                    <div className="arl-status-tabs" role="tablist" aria-label="Request status">
                        {QUICK_FILTERS.map((item) => {
                            const count = Number(statusCounts[item.value] || 0);
                            return (
                                <button
                                    key={item.value}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeStatus === item.value}
                                    className={`arl-status-tab ${activeStatus === item.value ? "is-active" : ""}`}
                                    onClick={() => handleQuickFilter(item.value)}
                                >
                                    <span className={`arl-status-tab-dot arl-status-${item.value.toLowerCase().replace("_","-")}`}/>
                                    <span>{item.label}</span>
                                    <span className="arl-status-tab-count">{count}</span>
                                </button>
                            );
                        })}
                    </div>
                }
                extra={<Text className="arl-sub">{pagination.total.toLocaleString()} requests</Text>}
            >
                <Table
                    className="arl-table"
                    rowKey="id"
                    rowClassName={(record) => {
                        const s = normalize(record.status);
                        if (s === "RETURNED") return "asset-row-returned";
                        if (s === "APPROVED") return "asset-row-approved";
                        return "";
                    }}
                    loading={loading}
                    columns={columns}
                    dataSource={data}
                    scroll={{x: 1250}}
                    sticky
                    onRow={(record) => ({
                        style: {"--row-accent": ACCENT_HEX[STATUS_COLORS[normalize(record.status)] || "default"]}
                    })}
                    expandable={{
                        expandedRowRender: (record) => <ExpandedRow record={record}/>,
                        expandIcon: ({expanded, onExpand, record}) => (
                            <Button
                                type="text"
                                size="small"
                                className="arl-expand-btn"
                                icon={expanded ? <DownOutlined/> : <RightOutlined/>}
                                onClick={(e) => onExpand(record, e)}
                            />
                        )
                    }}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} requests`
                    }}
                    onChange={tableChange}
                    size="middle"
                />
            </Card>

            <Drawer
                title={selected?.request_no || (selected ? `Asset Request #${selected.id}` : "Asset Request")}
                width={620}
                open={openDrawer}
                onClose={() => setOpenDrawer(false)}
                className="arl-drawer"
            >
                {selected && (
                    <>
                        <Descriptions bordered size="small" column={1}>
                            <Descriptions.Item label="Asset Type">
                                {selected.asset_type || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Local">
                                {entityText(selected, "local")}
                            </Descriptions.Item>
                            <Descriptions.Item label="District">
                                {entityText(selected, "district")}
                            </Descriptions.Item>
                            <Descriptions.Item label="Area">
                                {entityText(selected, "region")}
                            </Descriptions.Item>
                            <Descriptions.Item label="Request Date">
                                {selected.request_date
                                    ? dayjs(selected.request_date).format(CisUI().dateFormat)
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Required By">
                                {selected.required_by_date
                                    ? dayjs(selected.required_by_date).format(CisUI().dateFormat)
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Priority">
                                {selected.priority_level || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Acquisition Method">
                                {selected.acquisition_method || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Estimated Cost">
                                {money(selected.estimated_cost)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Current Level">
                                {LEVEL_LABELS[normalize(selected.current_level)] || selected.current_level || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Status">
                                {selected.status || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Created By">
                                {userName(selected, "created_by")}
                            </Descriptions.Item>
                            <Descriptions.Item label="Created At">
                                {dateTimeText(selected.created_at)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Last Action">
                                {selected.last_action ? prettyStatus(selected.last_action) : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Last Action By">
                                {selected.last_action_by_name || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Last Action At">
                                {dateTimeText(selected.last_action_at)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Approved By">
                                {approvalDisplay(selected).name}
                            </Descriptions.Item>
                        </Descriptions>

                        <Space style={{marginTop: 20}}>
                            <Button
                                icon={<EyeOutlined/>}
                                onClick={() => history.push(`/assets/assetrequestdetail/${selected.id}`)}
                            >
                                Open Full Request
                            </Button>

                            {canEdit(selected) && (
                                <Button
                                    type="primary"
                                    icon={<EditOutlined/>}
                                    onClick={() => editRequest(selected)}
                                >
                                    Edit
                                </Button>
                            )}

                            {getApprovalAction(selected) && (
                                <Button
                                    type="primary"
                                    icon={<CheckCircleOutlined/>}
                                    onClick={() => {
                                        setOpenDrawer(false);
                                        showApproval(selected);
                                    }}
                                >
                                    {getApprovalAction(selected).label}
                                </Button>
                            )}
                        </Space>
                    </>
                )}
            </Drawer>

            <Modal
                open={approvalModal}
                title={selected ? getApprovalAction(selected)?.label : "Approve Request"}
                okText={normalize(selected?.current_level) === "NATIONAL"
                    ? "Approve"
                    : "Approve & Forward"}
                confirmLoading={actionLoading}
                onOk={confirmApproval}
                onCancel={() => {
                    if (!actionLoading) setApprovalModal(false);
                }}
                className="arl-approve-modal"
                destroyOnClose
            >
                {selected && (
                    <>
                        <Descriptions size="small" bordered column={1}>
                            <Descriptions.Item label="Request">
                                {selected.request_no || `#${selected.id}`}
                            </Descriptions.Item>
                            <Descriptions.Item label="Asset">
                                {selected.asset_type || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Entity">
                                {entitySearchText(selected)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Current Level">
                                {LEVEL_LABELS[normalize(selected.current_level)] || selected.current_level}
                            </Descriptions.Item>
                            <Descriptions.Item label="Estimated Cost">
                                {money(selected.estimated_cost)}
                            </Descriptions.Item>
                        </Descriptions>

                        <Form.Item
                            label={normalize(selected.current_level) === "NATIONAL"
                                ? "Approval Comments"
                                : "Assessment / Feedback"}
                            style={{marginTop: 18, marginBottom: 0}}
                        >
                            <TextArea
                                rows={4}
                                value={approvalComments}
                                onChange={(e) => setApprovalComments(e.target.value)}
                                placeholder={
                                    normalize(selected.current_level) === "NATIONAL"
                                        ? "Add final approval comments..."
                                        : "Add assessment or feedback before forwarding..."
                                }
                            />
                        </Form.Item>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default AssetRequestList;
